import { DocumentReference, Bundle } from "fhir/r5";
import Client from "fhir-kit-client";
import { Buffer } from "buffer";
import UserService from "./UserService";

////////////////////////////
//          Client        //
////////////////////////////

const fhirClient = createFhirClient();

function createFhirClient(): Client {
  const baseUrl = process.env.REACT_APP_DOCUMENT_URL ?? "fhir";
  return new Client({ baseUrl });
}

/////////////////////////////////////
//            Types                //
/////////////////////////////////////

export type PandoraMode = "full" | "light";

export type PandoraEnvironment = {
  id: string;
  name: string;
  validateurUrl: string;
  messageUrl: string;
};

export type UserConfiguration = {
  pandoraMode?: PandoraMode;
  currentEnvironmentId?: string;
  environments?: PandoraEnvironment[];
};

/////////////////////////////////////
//            Helpers              //
/////////////////////////////////////

export function getCurrentEnvironment(
  config?: UserConfiguration
): PandoraEnvironment | undefined {
  if (!config?.environments || config.environments.length === 0) {
    return undefined;
  }

  return config.environments.find(
    (env) => env.id === config.currentEnvironmentId
  );
}

export function getValidationUrl(config?: UserConfiguration): string | undefined {
  return getCurrentEnvironment(config)?.validateurUrl;
}

export function getMessageUrl(config?: UserConfiguration): string | undefined {
  return getCurrentEnvironment(config)?.messageUrl;
}

/////////////////////////////////////
//       Business methods          //
/////////////////////////////////////

export async function getUserConfiguration(): Promise<{
  documentReferenceId?: string;
  config: UserConfiguration;
}> {
  if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_E2E_MODE === "true") {
    return { config: { pandoraMode: "full", environments: [] } };
  }

  const username = UserService.getUsername();

  if (!username) {
    throw new Error("User is not authenticated");
  }

  const results = await searchDocumentReference(username);

  // Config found (exactly one expected)
  if (results.length === 1) {
    const dr = results[0];

    const base64 = dr.content?.[0]?.attachment?.data;
    if (!base64) {
      //No content found for the configuration
      throw new Error("No content for configuration found in DocumentReference/" + dr.id);
    }

    //Parsing configuration content
    var userConfiguration: UserConfiguration;
    try {
      const json = Buffer.from(base64, "base64").toString();
      userConfiguration = JSON.parse(json) as UserConfiguration;
    } catch (error) {
      console.log(error);
      throw new Error("Cannot read content of DocumentReference/" + dr.id);
    };
    
    //Validate stored environments.
    if (!userConfiguration.environments || userConfiguration.environments.length === 0) {
      throw new Error("No environment defined in DocumentReference/" + dr.id);
    }

    //Check environment validity
    const invalidEnvs = userConfiguration.environments.filter(
      (env) => !env.id || !env.name || !env.validateurUrl || !env.messageUrl
    );

    if (invalidEnvs.length !== 0) {
      throw new Error("Invalid environments in DocumentReference/" + dr.id);
    }

    var environments = userConfiguration.environments;

    if (userConfiguration.currentEnvironmentId) {
      const exists = environments.find(
        (e) => e.id === userConfiguration.currentEnvironmentId
      );

      if (!exists) {
        throw new Error("Cannot find current environment " + userConfiguration.currentEnvironmentId + " in DocumentReference/" + dr.id);
      }
    } else {
      userConfiguration.currentEnvironmentId = userConfiguration.environments[0].id;
    }

    return {
      documentReferenceId: dr.id,
      config: userConfiguration,
    };
  }

  //Error cases
  // No config found 
  if (results.length === 0) {
    throw new Error("No configuration found");
  }
  // Too many config found
  throw new Error("Too many configurations found");
}

export async function updateUserConfiguration(
  documentReferenceId: string,
  config: UserConfiguration
): Promise<void> {
  const base64 = Buffer.from(JSON.stringify(config)).toString("base64");

  const documentReference: DocumentReference = {
    resourceType: "DocumentReference",
    id: documentReferenceId,
    status: "current",
    subject: {
      display: UserService.getUsername(),
    },
    type: {
      coding: [
        {
          system: "urn:olympus:config-type",
          code: "user-config",
        },
      ],
    },
    category: [
      {
        coding: [
          {
            system: "urn:olympus:app",
            code: "pandora",
          },
        ],
      },
    ],
    identifier: [
      {
        system: "urn:olympus:app-id",
        value: "pandora",
      },
      {
          "system": "urn:olympus:user-id",
          "value": UserService.getUsername()
      }
    ],
    content: [
      {
        attachment: {
          contentType: "application/json",
          data: base64,
        },
      },
    ],
  };

  await fhirClient.update({
    resourceType: "DocumentReference",
    id: documentReferenceId,
    body: documentReference,
  });
}

/////////////////////////////////////
//          FHIR calls             //
/////////////////////////////////////

async function searchDocumentReference(userId: string): Promise<DocumentReference[]> {
  const response = await fhirClient.search({
    resourceType: "DocumentReference",
    searchParams: {
      type: "user-config",
      category: "pandora",
      identifier: [
        "urn:olympus:app-id|pandora",
        "urn:olympus:user-id|" + userId
      ],
      status: "current",
    },
  });

  if (response.resourceType !== "Bundle") {
    throw Error(response.statusText);
  }

  const bundle: Bundle = response as Bundle;

  return (
    bundle.entry
      ?.filter((e) => e.resource?.resourceType === "DocumentReference")
      .map((e) => e.resource as DocumentReference) ?? []
  );
}

export async function createDocumentReference(
  userId: string,
  config: UserConfiguration
): Promise<DocumentReference> {
  const base64 = Buffer.from(JSON.stringify(config)).toString("base64");

  const documentReference: DocumentReference = {
    resourceType: "DocumentReference",
    status: "current",
    subject: {
      display: userId,
    },
    type: {
      coding: [
        {
          system: "urn:olympus:config-type",
          code: "user-config",
        },
      ],
    },
    category: [
      {
        coding: [
          {
            system: "urn:olympus:app",
            code: "pandora",
          },
        ],
      },
    ],
    identifier: [
      {
        system: "urn:olympus:app-id",
        value: "pandora",
      },
      {
        system: "urn:olympus:user-id",
        value: userId,
      },
    ],
    content: [
      {
        attachment: {
          contentType: "application/json",
          data: base64,
        },
      },
    ],
  };

  return (await fhirClient.create({
    resourceType: "DocumentReference",
    body: documentReference,
  })) as DocumentReference;
}
